import { useMemo, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Counter from 'yet-another-react-lightbox/plugins/counter'
import Download from 'yet-another-react-lightbox/plugins/download'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/counter.css'
import {
  cmsImageFallbackUrl,
  cmsImageOriginalUrl,
  cmsImageSrcSet,
  cmsImageUrl,
  cmsImageVariantUrl,
} from '../lib/cms-image'
import { handleCmsImageError } from '../lib/img-fallback'
import { absoluteUrl } from '../lib/site-origin'

type Props = {
  urls: string[]
  title?: string
}

/** Deep-link target so the home page can jump straight to the photos. */
export const EVENT_GALLERY_ANCHOR = 'galerie'

/**
 * Grid tiles are square and span 50vw / 33vw / 25vw as the layout widens, so a
 * tile is at most ~360 CSS px. The widths cover 1x through 3x device pixel
 * ratios; the browser picks one from `sizes` instead of pulling the original.
 */
const THUMB_WIDTHS = [320, 480, 640, 960] as const
const THUMB_SIZES = '(min-width:900px) 25vw, (min-width:480px) 33vw, 50vw'

function downloadName(url: string, index: number): string {
  const base = url.split('/').pop()?.replace(/\?.*$/, '') || `bnkr-${index + 1}.jpg`
  return base.includes('.') ? base : `${base}.jpg`
}

export function EventGallery({ urls, title = 'Impressionen' }: Props) {
  const [index, setIndex] = useState(-1)

  const slides = useMemo(
    () =>
      urls.map((raw, i) => {
        // Legacy WebP variants may 404; the JPEG original is the kept file.
        const stored = cmsImageOriginalUrl(raw) ?? raw
        const original = cmsImageFallbackUrl(stored) ?? stored
        // On screen: a 1920px variant (a few hundred KB) instead of the multi-MB
        // original — sharp on any display, including zoom. The download button
        // still hands out the untouched full-resolution file.
        const src = cmsImageVariantUrl(original, 1920) ?? original
        return {
          src: absoluteUrl(src),
          download: { url: absoluteUrl(original), filename: downloadName(original, i) },
        }
      }),
    [urls],
  )

  if (!urls.length) return null

  return (
    <section className="ev-impressions" id={EVENT_GALLERY_ANCHOR} aria-label={title}>
      <div className="ev-impressions__head">
        <h2 className="ev-impressions__title">{title}</h2>
        <span className="ev-impressions__count muted" style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
          {urls.length} Fotos
        </span>
      </div>

      <div className="ev-gallery-grid" role="list">
        {urls.map((url, i) => {
          const thumb = cmsImageUrl(url, 'thumb') ?? url
          const srcSet = cmsImageSrcSet(url, THUMB_WIDTHS)
          return (
            <button
              key={`${url}-${i}`}
              type="button"
              className="ev-gallery-grid__item"
              role="listitem"
              aria-label={`Foto ${i + 1} von ${urls.length} öffnen`}
              onClick={() => setIndex(i)}
            >
              <img
                src={thumb}
                srcSet={srcSet}
                sizes={srcSet ? THUMB_SIZES : undefined}
                alt=""
                loading="lazy"
                decoding="async"
                onError={(e) => handleCmsImageError(e, '.ev-gallery-grid__item')}
              />
            </button>
          )
        })}
      </div>

      <Lightbox
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        slides={slides}
        plugins={[Counter, Zoom, Download]}
        carousel={{ finite: urls.length <= 1 }}
        controller={{ closeOnBackdropClick: true }}
        render={{
          buttonPrev: urls.length <= 1 ? () => null : undefined,
          buttonNext: urls.length <= 1 ? () => null : undefined,
        }}
        styles={{
          container: { backgroundColor: 'rgba(0,0,0,.94)' },
        }}
      />
    </section>
  )
}
